const KEYBOARD_STEP = 16;

export type LayoutDrawerResizeBehaviour = {
  onResize: (width: number) => void;
};

/**
 * The drawer is docked to the inline end, so dragging or arrowing toward the
 * inline start widens it. Pointer events rather than mouse events, because
 * `mousedown` never fires for touch or pen: on a tablet the mouse-only handle
 * was inert. Pointer capture also keeps every move on this element, so there
 * are no document-level listeners to leak and no body style to mutate and
 * restore.
 */
export const delegateLayoutDrawerResize = (handle: HTMLElement, { onResize }: LayoutDrawerResizeBehaviour) => {
  const value = (name: 'aria-valuemax' | 'aria-valuemin' | 'aria-valuenow') => Number(handle.getAttribute(name));
  const clamp = (width: number) => Math.min(value('aria-valuemax'), Math.max(value('aria-valuemin'), width));
  let drag: { startWidth: number; startX: number } | null = null;

  const handlePointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    drag = { startWidth: value('aria-valuenow'), startX: event.clientX };
    handle.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent) => {
    const started = drag;
    if (!started || !handle.parentElement) return;
    handle.parentElement.style.setProperty('--slot-width', `${clamp(started.startWidth + started.startX - event.clientX)}px`);
  };

  const handlePointerUp = (event: PointerEvent) => {
    const started = drag;
    if (!started) return;
    drag = null;
    handle.releasePointerCapture(event.pointerId);
    onResize(clamp(started.startWidth + started.startX - event.clientX));
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const current = value('aria-valuenow');
    const width = {
      ArrowLeft: () => clamp(current + KEYBOARD_STEP),
      ArrowRight: () => clamp(current - KEYBOARD_STEP),
      End: () => value('aria-valuemin'),
      Home: () => value('aria-valuemax'),
    }[event.key];
    if (!width) return;
    event.preventDefault();
    onResize(width());
  };

  handle.addEventListener('pointerdown', handlePointerDown);
  handle.addEventListener('pointermove', handlePointerMove);
  handle.addEventListener('pointerup', handlePointerUp);
  handle.addEventListener('pointercancel', handlePointerUp);
  handle.addEventListener('keydown', handleKeyDown);
  return () => {
    handle.removeEventListener('pointerdown', handlePointerDown);
    handle.removeEventListener('pointermove', handlePointerMove);
    handle.removeEventListener('pointerup', handlePointerUp);
    handle.removeEventListener('pointercancel', handlePointerUp);
    handle.removeEventListener('keydown', handleKeyDown);
  };
};
